import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { Area } from 'react-easy-crop/types';

interface CropModalProps {
  imageUrl: string;
  imageName: string;
  onCropComplete: (cropArea: { x: number; y: number; width: number; height: number }) => void;
  onCancel: () => void;
}

const CropModal: React.FC<CropModalProps> = ({
  imageUrl,
  imageName,
  onCropComplete,
  onCancel
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [imageSize, setImageSize] = useState<{ width: number; height: number } | null>(null);

  const onCropChange = (location: { x: number; y: number }) => {
    setCrop(location);
  };

  const onCropAreaComplete = useCallback((croppedArea: Area, croppedAreaPixels: Area) => {
    console.log('onCropAreaComplete - croppedArea:', croppedArea);
    console.log('onCropAreaComplete - croppedAreaPixels:', croppedAreaPixels);
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const onMediaLoaded = useCallback((mediaSize: { width: number; height: number; naturalWidth: number; naturalHeight: number }) => {
    console.log('Image loaded with size:', mediaSize);
    // Use natural dimensions for crop calculations, not display dimensions
    setImageSize({ width: mediaSize.naturalWidth, height: mediaSize.naturalHeight });
  }, []);

  const handleApply = () => {
    if (!croppedAreaPixels || !imageSize) {
      alert('Please wait for the image to load');
      return;
    }

    console.log('Crop area pixels:', croppedAreaPixels);
    console.log('Image size:', imageSize);

    // Convert pixel coordinates to normalized coordinates (0.0-1.0)
    const normalizedCrop = {
      x: croppedAreaPixels.x / imageSize.width,
      y: croppedAreaPixels.y / imageSize.height,
      width: croppedAreaPixels.width / imageSize.width,
      height: croppedAreaPixels.height / imageSize.height
    };

    console.log('Normalized crop (before bounds):', normalizedCrop);

    // Ensure values are within bounds
    normalizedCrop.x = Math.max(0, Math.min(normalizedCrop.x, 1));
    normalizedCrop.y = Math.max(0, Math.min(normalizedCrop.y, 1));
    normalizedCrop.width = Math.max(0, Math.min(normalizedCrop.width, 1 - normalizedCrop.x));
    normalizedCrop.height = Math.max(0, Math.min(normalizedCrop.height, 1 - normalizedCrop.y));

    console.log('Normalized crop (after bounds):', normalizedCrop);

    // Validate that we have a valid crop area
    if (normalizedCrop.width === 0 || normalizedCrop.height === 0) {
      alert('Invalid crop area. Please adjust your selection and try again.');
      return;
    }

    onCropComplete(normalizedCrop);
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        zIndex: 2000,
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '16px 20px',
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
          color: 'white'
        }}
      >
        <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>
          Crop Image: {imageName}
        </h2>
        <p style={{ margin: '8px 0 0 0', fontSize: '14px', color: 'rgba(255, 255, 255, 0.7)' }}>
          Drag image to reposition • Use zoom slider • Drag crop box corners to resize • Click "Apply" when ready
        </p>
      </div>

      {/* Cropper Area */}
      <div style={{ position: 'relative', flex: 1, minHeight: 0 }}>
        <Cropper
          image={imageUrl}
          crop={crop}
          zoom={zoom}
          aspect={undefined}
          onCropChange={onCropChange}
          onCropComplete={onCropAreaComplete}
          onZoomChange={setZoom}
          onMediaLoaded={onMediaLoaded}
          showGrid={true}
          restrictPosition={false}
          cropSize={{ width: 400, height: 400 }}
          style={{
            containerStyle: {
              backgroundColor: 'var(--photo-backdrop)'
            },
            cropAreaStyle: {
              border: '2px solid var(--border-light)',
              color: 'rgba(255, 255, 255, 0.5)'
            }
          }}
        />
      </div>

      {/* Controls */}
      <div
        style={{
          padding: '20px',
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          borderTop: '1px solid rgba(255, 255, 255, 0.2)'
        }}
      >
        {/* Zoom Slider */}
        <div style={{ marginBottom: '20px' }}>
          <label
            style={{
              display: 'block',
              color: 'white',
              fontSize: '14px',
              marginBottom: '8px',
              fontWeight: 500
            }}
          >
            Zoom: {zoom.toFixed(1)}x
          </label>
          <input
            type="range"
            min={1}
            max={3}
            step={0.1}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            style={{
              width: '100%',
              maxWidth: '400px'
            }}
          />
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            onClick={onCancel}
            style={{
              padding: '10px 24px',
              fontSize: '14px',
              fontWeight: 500,
              border: '1px solid rgba(255, 255, 255, 0.3)',
              borderRadius: '6px',
              backgroundColor: 'transparent',
              color: 'white',
              cursor: 'pointer',
              transition: 'all 0.15s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleApply}
            style={{
              padding: '10px 24px',
              fontSize: '14px',
              fontWeight: 500,
              border: 'none',
              borderRadius: '6px',
              backgroundColor: 'var(--primary)',
              color: 'var(--text-inverse)',
              cursor: 'pointer',
              transition: 'all 0.15s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--error)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--primary)';
            }}
          >
            Apply Crop
          </button>
        </div>
      </div>
    </div>
  );
};

export default CropModal;
